import styled from 'styled-components';
import { getColor } from 'utils';
import { hexToRgba } from 'theme';
import constants from 'constants/index';

const light = constants.THEME.LIGHT;

// darkGrey reads as muted on the light theme only; the dark theme needs lightGrey to stay legible.
const muted = props => getColor(props, props.$selectedTheme === light ? 'darkGrey' : 'lightGrey');

export const ModalSubtitle = styled('div')`
  font-size: 0.85rem;
  color: ${muted};
  padding-bottom: 0.25rem;
`;

export const ScrollArea = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 50vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0.5rem 0.25rem;
`;

export const SectionLabel = styled('div')`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${props => getColor(props, 'onPrimary')};
`;

export const SectionHint = styled('div')`
  font-size: 0.8rem;
  color: ${muted};
`;

export const SectionDivider = styled('hr')`
  width: 100%;
  border: 0;
  border-top: 1px solid ${props => getColor(props, 'mediumGrey')};
  margin: 0.75rem 0 0.25rem;
`;

export const FormatRow = styled('div')`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 0.35rem;
  border: 1px solid ${props => (
    props.$selected ? getColor(props, 'accent') : getColor(props, 'mediumGrey')
  )};
  background-color: ${props => (
    props.$selected ? hexToRgba(getColor(props, 'accent'), 0.08) : getColor(props, 'bgCell')
  )};
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  transition: background-color 0.2s ease, border-color 0.2s ease;

  &:hover {
    background-color: ${props => {
      if (!props.$clickable) return getColor(props, 'bgCell');
      return hexToRgba(getColor(props, 'accent'), props.$selected ? 0.12 : 0.05);
    }};
  }

  &:focus-visible {
    outline: 2px solid ${props => getColor(props, 'accent')};
    outline-offset: 2px;
  }
`;

export const FormatIcon = styled('div')`
  display: flex;
  align-items: center;
  color: ${props => props.$muted ? muted(props) : getColor(props, 'accent')};
`;

// flex-basis keeps the name on its own line once the row is too narrow for the actions beside it.
export const FormatName = styled('div')`
  flex: 1 1 9rem;
  font-size: 0.9rem;
  color: ${props => props.$muted ? muted(props) : getColor(props, 'onPrimary')};
  overflow-wrap: anywhere;
`;

export const RowActions = styled('div')`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-left: auto;
`;

export const StatusBadge = styled('div')`
  font-size: 0.75rem;
  white-space: nowrap;
  padding: 0.2rem 0.6rem;
  border-radius: 1rem;
  color: ${props => getColor(props, 'accent')};
  background-color: ${props => hexToRgba(getColor(props, 'accent'), 0.12)};
`;

// One button for both actions so Download and Publish rows line up at every state; min-width holds
// the width steady when the label swaps to Downloading… / Publishing….
export const RowButton = styled('button')`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  min-width: 9rem;
  height: 2.1rem;
  font-size: 0.8rem;
  white-space: nowrap;
  padding: 0 1.1rem;
  border-radius: 0.25rem;
  border: 1px solid ${props => getColor(props, 'accent')};
  background-color: ${props => getColor(props, 'transparent')};
  color: ${props => getColor(props, 'accent')};
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${props => hexToRgba(getColor(props, 'accent'), 0.1)};
  }

  &:focus-visible {
    outline: 2px solid ${props => getColor(props, 'accent')};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    border-color: ${props => getColor(props, 'mediumGrey')};
    color: ${muted};
    background-color: ${props => getColor(props, 'transparent')};
  }
`;

export const RowError = styled('div')`
  flex: 1 1 100%;
  font-size: 0.75rem;
  color: ${props => getColor(props, 'error')};
`;

export const RowNote = styled('div')`
  flex: 1 1 100%;
  font-size: 0.75rem;
  color: ${muted};
`;

export const EmptyState = styled('div')`
  font-size: 0.85rem;
  padding: 1rem;
  text-align: center;
  color: ${muted};
`;

export const ModalFooter = styled('div')`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 1rem;
  padding-top: 1rem;
  margin-top: 0.5rem;
  border-top: 1px solid ${props => getColor(props, 'mediumGrey')};
`;

export const FooterLink = styled('a')`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.85rem;
  color: ${props => getColor(props, 'accent')};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export const CancelButton = styled('button')`
  font-size: 0.85rem;
  padding: 0.6rem 1.5rem;
  border-radius: 0.25rem;
  border: 1px solid ${props => getColor(props, 'mediumGrey')};
  background-color: ${props => getColor(props, 'transparent')};
  color: ${props => getColor(props, 'onPrimary')};
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${props => getColor(props, 'lightGrey')};
  }
`;
